import { useState } from "react";
import { toast } from "sonner";

import { lookupArticles } from "@/lib/order-import/api";
import type { LoadedFile } from "@/lib/order-import/detect-columns";

/**
 * Reads and recognises an order file (see loadOrderFile). Unreadable files and files
 * without an order table end in a toast; `onLoaded` only gets a usable file. Shared by
 * the file button and drag-and-drop onto the import table.
 */
export function useOrderFileReader(onLoaded: (file: LoadedFile) => void) {
  const [reading, setReading] = useState(false);

  const read = async (file: File) => {
    setReading(true);
    try {
      // Loaded on first use: the buttons sit on the eagerly loaded dashboard.
      const { loadOrderFile } = await import("@/lib/order-import/load-file");
      const loaded = await loadOrderFile(file, lookupArticles);
      if (!loaded) {
        toast.error("В файле не найдено столбцов с артикулом и количеством", {
          description: "Нужен столбец с артикулами и столбец с количеством в штуках.",
        });
        return;
      }
      onLoaded(loaded);
    } catch (error) {
      // ImportFileError (by name: the class lives in the lazily loaded module).
      toast.error(
        error instanceof Error && error.name === "ImportFileError"
          ? error.message
          : "Не удалось прочитать файл",
      );
    } finally {
      setReading(false);
    }
  };

  return { reading, read };
}
